import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { t } from 'ttag';
import update from 'immutability-helper';

import { memberSettingsSafePaymentMethods } from '@urls';
import { getFieldsSingleError } from '@utils/common';
import { UPDATE_PAYMENT_METHOD_ERROR_CODES } from '@constants';
import { PaymentMethodToggle, PaymentMethod } from '@components/settings/PaymentMethodToggle';
import { PaymentMethodErrorModal } from '@components/settings/PaymentMethodErrorModal';
import { PrepaymentEditFormModal, FormData as PrepaymentFormData } from '@components/settings/PrepaymentEditFormModal';
import { VerifySafePaymentDisableModal } from '@components/settings/VerifySafePaymentDisableModal';
import { SafePaymentMethodRequiredErrorModal } from '@components/settings/SafePaymentMethodRequiredErrorModal';
import { ShopSettingsDeliveryMethodsDocument } from '@containers/settings/ShopSettingsDeliveryMethods/graphql.v4.generated';
import {
  ShopSettingsPaymentMethodsDocument,
  ShopSettingsPaymentMethodsQuery,
} from '@containers/settings/ShopSettingsPaymentMethods/gql/graphql.v4.generated';
import { PaymentMethodEnum } from '@newModels';

import {
  PaymentMethodPrepaymentDocument,
  PaymentMethodPrepaymentQuery,
  usePaymentMethodPrepaymentQuery,
  useUpdatePaymentMethodsMutation,
} from './gql/graphql.v4.generated';


interface Props {
  className?: string,
  method: PaymentMethod,
  isRozetkaDeliveryEnabled: boolean,
}

export const PaymentMethodToggleContainer: React.FC<Props> = ({
  className,
  method,
  isRozetkaDeliveryEnabled,
}) => {
  const client = useApolloClient();
  const history = useHistory();
  const [errorText, setErrorText] = useState<string>();
  const [isPayLaterEditModalOpen, setIsPayLaterEditModalOpen] = useState(false);
  const [isDisableSafePaymentModalOpen, setIsDisableSafePaymentModalOpen] = useState(false);
  const [isAddPaymentCardModalOpen, setIsAddPaymentCardModalOpen] = useState(false);

  const { data: prepaymentData } = usePaymentMethodPrepaymentQuery({
    context: {
      v4: true,
    },
  });

  const [updatePaymentMethods, { loading: isSubmitting }] = useUpdatePaymentMethodsMutation({
    context: { v4: true },
    refetchQueries: [
      // When disable safe payment, Rozetka delivery method may be disabled
      // on backend side. So we need to refetch delivery methods to update
      ShopSettingsDeliveryMethodsDocument,
    ],
  });

  const isLastEnabledPaymentMethod = () => {
    const data = client.readQuery<ShopSettingsPaymentMethodsQuery>({
      query: ShopSettingsPaymentMethodsDocument,
    });

    if (data?.viewer?.paymentMethods) {
      for (const paymentMethod of data.viewer.paymentMethods) {
        if (paymentMethod.isEnabled && paymentMethod.id !== method.id) {
          return false;
        }
      }
    }

    return true;
  };

  const handleUpdatePaymentMethod = (checked: boolean, newPrepayment?: number) => {
    const prepayment = newPrepayment
      ? String(newPrepayment)
      : prepaymentData?.viewer?.prepayment;

    const optimisticResponse = {
      updatePaymentMethods: {
        paymentMethod: {
          id: method.id,
          isEnabled: checked,
          isHidden: method.isHidden,
          name: method.name,
          icon: method.icon,
          description: method.description,
          descriptionIfHidden: method.descriptionIfHidden,
          __typename: 'UserPaymentInfo' as const,
        },
        errors: null,
        __typename: 'UpdatePaymentMethodsResponse' as const,
      },
    };

    return updatePaymentMethods({
      variables: {
        paymentMethod: method.id,
        value: checked,
        prepayment,
      },
      optimisticResponse: () => optimisticResponse,
    });
  };

  const handleToggle = async (checked: boolean, verified = false) => {
    if (!checked) {
      if (isLastEnabledPaymentMethod()) {
        setErrorText(t`У вас має залишитися хоча б один спосіб оплати.`);
        return;
      }

      if (!verified && method.id === PaymentMethodEnum.SafePayment && isRozetkaDeliveryEnabled) {
        setIsDisableSafePaymentModalOpen(true);
        return;
      }
    }

    const response = await handleUpdatePaymentMethod(checked);

    if (response?.data?.updatePaymentMethods?.errors) {
      const errors = response?.data?.updatePaymentMethods?.errors;
      const error = errors[0].messages[0];
      const errorCode = error.code;

      if (errorCode === UPDATE_PAYMENT_METHOD_ERROR_CODES.PAYMENT_CARD_REQUIRED) {
        setIsAddPaymentCardModalOpen(true);
      } else {
        setErrorText(error.message);
      }
    }
  };

  const handleUpdatePrepayment = async (formData: PrepaymentFormData) => {
    const response = await handleUpdatePaymentMethod(method.isEnabled, formData.prepayment);
    const errors = response.data?.updatePaymentMethods?.errors;

    if (errors?.length) {
      return getFieldsSingleError(errors);
    }

    const cacheData = client.cache.readQuery<PaymentMethodPrepaymentQuery>(
      { query: PaymentMethodPrepaymentDocument },
    )!;

    client.cache.writeQuery({
      query: PaymentMethodPrepaymentDocument,
      data: update(
        cacheData,
        {
          viewer: {
            prepayment: {
              $set: String(formData.prepayment),
            },
          },
        },
      ),
    });

    setIsPayLaterEditModalOpen(false);
    return null;
  };

  const handleSafePaymentMethodEdit = () => {
    history.push({
      pathname: memberSettingsSafePaymentMethods,
    });
  };

  return (
    <>
      <PaymentMethodToggle
        className={className}
        method={method}
        prepayment={Number(prepaymentData?.viewer?.prepayment) || 0}
        isSubmitting={isSubmitting}
        onToggle={handleToggle}
        onPayLaterEdit={() => setIsPayLaterEditModalOpen(true)}
        onSafePaymentEdit={handleSafePaymentMethodEdit}
      />
      <PaymentMethodErrorModal
        isOpen={!!errorText}
        text={errorText || ''}
        onRequestClose={() => setErrorText(undefined)}
      />

      <VerifySafePaymentDisableModal
        isOpen={isDisableSafePaymentModalOpen}
        onRequestClose={() => setIsDisableSafePaymentModalOpen(false)}
        onConfirm={() => {
          handleToggle(false, true);
          setIsDisableSafePaymentModalOpen(false);
        }}
      />
      <PrepaymentEditFormModal
        isOpen={isPayLaterEditModalOpen}
        prepayment={Number(prepaymentData?.viewer?.prepayment) || 0}
        isSubmitting={isSubmitting}
        onRequestClose={() => setIsPayLaterEditModalOpen(false)}
        onSubmit={handleUpdatePrepayment}
      />

      <SafePaymentMethodRequiredErrorModal
        isOpen={isAddPaymentCardModalOpen}
        onConfirm={() => history.push({ pathname: memberSettingsSafePaymentMethods })}
        onRequestClose={() => setIsAddPaymentCardModalOpen(false)}
      />
    </>
  );
};
